/**
 * Activités réalisées — §2. Saisie manuelle d'une activité (course, muscu,
 * golf…) et historique. La charge d'entraînement est calculée côté serveur si
 * elle n'est pas fournie ; une activité saisie peut se rapprocher
 * automatiquement d'une séance planifiée du même sport (prévu ↔ réalisé).
 *
 * Les sources externes (Apple Health, Health Connect, Strava, Garmin) se gèrent
 * dans Réglages ; ici on couvre la saisie directe, toujours disponible.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

import { performanceApi } from '../api/performanceApi'
import {
  Loading,
  PERF_ACCENT,
  Pill,
  SectionTitle,
  cardSx,
  darkFieldSx,
} from './shared'

const ACTIVITY_TYPES = {
  run: 'Course',
  ride: 'Vélo',
  swim: 'Natation',
  strength: 'Musculation',
  yoga: 'Yoga',
  ski: 'Ski',
  golf: 'Golf',
  walk: 'Marche',
  hike: 'Randonnée',
  rowing: 'Aviron / rameur',
  hiit: 'HIIT',
  other: 'Autre',
}

const PROVIDER_LABELS = {
  manual: 'Manuel',
  apple_health: 'Apple Health',
  health_connect: 'Health Connect',
  strava: 'Strava',
  garmin: 'Garmin',
  life_health: 'Santé Life',
}

const nowLocal = () => {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

const NEW_ACTIVITY = () => ({
  sport: '',
  activity_type: 'run',
  started_at: nowLocal(),
  duration_min: 45,
  distance_km: '',
  calories: '',
  avg_heart_rate: '',
  perceived_effort: '',
})

const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v))

export default function ActivitiesView() {
  const [activities, setActivities] = useState([])
  const [prefs, setPrefs] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(NEW_ACTIVITY())
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    const [a, p] = await Promise.all([
      performanceApi.activities.list(),
      performanceApi.preferences.list(),
    ])
    setActivities(a)
    setPrefs(p)
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const save = async () => {
    if (!form.started_at) return
    setBusy(true)
    try {
      const km = toNum(form.distance_km)
      await performanceApi.activities.create({
        sport: form.sport || null,
        activity_type: form.activity_type,
        started_at: new Date(form.started_at).toISOString(),
        duration_sec: Math.round((toNum(form.duration_min) || 0) * 60),
        distance_m: km != null ? Math.round(km * 1000) : null,
        calories: toNum(form.calories),
        avg_heart_rate: toNum(form.avg_heart_rate),
        perceived_effort: toNum(form.perceived_effort),
      })
      setAdding(false)
      setForm(NEW_ACTIVITY())
      await load()
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <Loading label="Chargement de vos activités…" />

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <SectionTitle>Activités réalisées</SectionTitle>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => { setForm(NEW_ACTIVITY()); setAdding(true) }}
          sx={{ color: PERF_ACCENT, textTransform: 'none' }}
        >
          Saisir une activité
        </Button>
      </Stack>

      {activities.length === 0 ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucune activité enregistrée. Saisissez vos séances réalisées
            manuellement, ou connectez une source (Strava, Garmin, Apple Health…)
            depuis Réglages.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {activities.map((a) => {
            const km = a.distance_m ? (a.distance_m / 1000).toFixed(1) : null
            const min = a.duration_sec ? Math.round(a.duration_sec / 60) : null
            return (
              <Box key={a.id} sx={{ ...cardSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"
                  flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                      {ACTIVITY_TYPES[a.activity_type] || a.activity_type}
                      {a.sport_name ? ` · ${a.sport_name}` : ''}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                      {new Date(a.started_at).toLocaleString('fr-FR')}
                      {min ? ` · ${min} min` : ''}
                      {km ? ` · ${km} km` : ''}
                      {a.avg_heart_rate ? ` · ${a.avg_heart_rate} bpm` : ''}
                      {a.training_load ? ` · charge ${a.training_load}` : ''}
                    </Typography>
                  </Box>
                  <Pill
                    label={PROVIDER_LABELS[a.provider] || a.provider}
                    color={a.provider === 'manual' ? 'rgba(255,255,255,0.5)' : '#3ddc97'}
                  />
                </Stack>
              </Box>
            )
          })}
        </Stack>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#11162a', color: '#fff' } }}>
        <DialogTitle>Saisir une activité</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Type"
                  value={form.activity_type}
                  onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))}
                  fullWidth
                  sx={darkFieldSx}
                >
                  {Object.entries(ACTIVITY_TYPES).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Sport (optionnel)"
                  value={form.sport}
                  onChange={(e) => setForm((f) => ({ ...f, sport: e.target.value }))}
                  fullWidth
                  sx={darkFieldSx}
                >
                  <MenuItem value="">—</MenuItem>
                  {prefs.map((p) => (
                    <MenuItem key={p.id} value={p.sport}>{p.sport_name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <TextField
              type="datetime-local"
              label="Début"
              value={form.started_at}
              onChange={(e) => setForm((f) => ({ ...f, started_at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={darkFieldSx}
            />
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <TextField type="number" label="Durée (min)" value={form.duration_min}
                  onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))}
                  fullWidth sx={darkFieldSx} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField type="number" label="Distance (km)" value={form.distance_km}
                  onChange={(e) => setForm((f) => ({ ...f, distance_km: e.target.value }))}
                  fullWidth sx={darkFieldSx} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField type="number" label="FC moy. (bpm)" value={form.avg_heart_rate}
                  onChange={(e) => setForm((f) => ({ ...f, avg_heart_rate: e.target.value }))}
                  fullWidth sx={darkFieldSx} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField type="number" label="RPE (1-10)" value={form.perceived_effort}
                  onChange={(e) => setForm((f) => ({ ...f, perceived_effort: e.target.value }))}
                  inputProps={{ min: 1, max: 10 }} fullWidth sx={darkFieldSx} />
              </Grid>
            </Grid>
            <TextField type="number" label="Calories (optionnel)" value={form.calories}
              onChange={(e) => setForm((f) => ({ ...f, calories: e.target.value }))}
              fullWidth sx={darkFieldSx} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Annuler
          </Button>
          <Button onClick={save} disabled={busy} variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
